import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Check, Play, Mail, House } from "lucide-react";
import { Reveal } from "../components/Chrome";
import { HOST, OSCAR_URL, TRANSFORMS, captureAttribution } from "../content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VIDEO_EMBED_URL = ""; // paste Oscar's replay video embed URL here (Vimeo/YouTube/GHL video host)
const BOOKING_EMBED_URL = "https://link.smallbusinessautomator.com/widget/booking/dIzmDGbSUwRwUCjP3XqV";

const VideoPlayer = () => (
  <div className="group relative aspect-video w-full overflow-hidden rounded-lg border border-white/10 transition-shadow duration-300 hover:shadow-[0_0_50px_rgba(93,143,196,0.25)] hover:border-[#5D8FC4]/50" data-testid="replay-video-player">
    {VIDEO_EMBED_URL ? (
      <iframe
        src={VIDEO_EMBED_URL}
        title="VA Purchasing Power Webinar Replay"
        className="absolute inset-0 w-full h-full"
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
      />
    ) : (
      <div className="absolute inset-0 bg-[#0b0f16] flex flex-col items-center justify-center">
        <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(circle at 50% 60%, rgba(93,143,196,0.18) 0%, transparent 60%)" }} />
        <button className="relative w-24 h-24 rounded-full bg-[#5D8FC4] flex items-center justify-center transition-transform duration-300 group-hover:scale-110" data-testid="replay-play-button" aria-label="Play replay">
          <Play className="w-10 h-10 text-[#070B11] ml-1" fill="#070B11" />
        </button>
        <p className="relative font-mono-c text-[11px] tracking-[0.3em] text-white/40 uppercase mt-8">Replay video coming soon</p>
      </div>
    )}
  </div>
);

export default function Replay() {
  const [showCalendar, setShowCalendar] = useState(false);

  useEffect(() => {
    document.title = "Webinar Replay — VA Purchasing Power";
    const attribution = captureAttribution();
    const email = new URLSearchParams(window.location.search).get("email");
    axios.post(`${API}/replay-view`, { ...attribution, email: email || null }).catch(() => {});
    return () => { document.title = "Oscar Tobar | VA Purchasing Power | Free Webinar"; };
  }, []);

  return (
    <div className="relative min-h-screen" data-testid="replay-page">
      <div className="max-w-[960px] mx-auto px-6 pt-16 md:pt-24 pb-24">
        <Reveal>
          <p className="font-mono-c text-[11px] tracking-[0.3em] text-[#5D8FC4] uppercase" data-testid="replay-eyebrow">
            Replay — Know Your Exact VA Purchasing Power
          </p>
          <p className="mt-3 text-white/55 text-[15px]">Missed the live session, or want to watch it again? It's below.</p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-8">
            <VideoPlayer />
            <p className="mt-4 text-sm text-white/55" data-testid="replay-caption">
              Oscar Tobar walks through your exact $0-down VA purchasing power — 45 minutes.
            </p>
          </div>
        </Reveal>

        <Reveal>
          <div className="mt-20">
            <h2 className="uppercase font-black tracking-tight text-2xl md:text-3xl">In Case You Want the Recap</h2>
            <ul className="mt-8 space-y-5">
              {TRANSFORMS.map((t, i) => (
                <li key={i} className="flex items-start gap-4" data-testid={`replay-recap-${i + 1}`}>
                  <ArrowRight className="w-5 h-5 text-[#5D8FC4] shrink-0 mt-1" strokeWidth={2.5} />
                  <p className="text-base text-white/80 leading-relaxed">{t}</p>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        <Reveal>
          <div className="mt-20 border border-white/10 bg-white/[0.02] p-8 md:p-10" data-testid="replay-booking-section">
            <h2 className="uppercase font-black tracking-tight text-2xl md:text-3xl">Ready to Get Your Personal <span className="text-[#5D8FC4]">Number?</span></h2>
            <p className="mt-5 text-white/55 leading-[1.8] max-w-2xl">
              The replay covers the general picture. Your actual purchasing power depends on your specific entitlement,
              credit, and timeline — that's a quick one-on-one conversation, not a form. Book a time that works and I'll
              walk through your numbers personally.
            </p>
            <button
              className="btn-accent text-sm mt-8 flex items-center gap-2"
              data-testid="replay-book-call-button"
              onClick={() => setShowCalendar(true)}
            >
              <Check className="w-4 h-4" strokeWidth={3} /> Book My 1-on-1 Purchasing Power Call
            </button>
            {showCalendar && (
              <div className="mt-8 border border-white/10 rounded-lg overflow-hidden" data-testid="replay-calendar-embed">
                {BOOKING_EMBED_URL ? (
                  <iframe src={BOOKING_EMBED_URL} title="Book a call with Oscar" className="w-full" style={{ minHeight: 700, border: "none" }} />
                ) : (
                  <div className="bg-[#0b0f16] p-12 text-center">
                    <p className="font-mono-c text-[11px] tracking-[0.3em] text-white/40 uppercase">Booking calendar coming soon</p>
                    <p className="text-white/50 text-sm mt-4">Oscar's GHL booking calendar will appear here once the embed link is added.</p>
                  </div>
                )}
              </div>
            )}
            <p className="mt-6 text-sm text-white/40 flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#5D8FC4]" />
              Or email me directly —{" "}
              <a href="mailto:oscar@greatwesternfunding.com" className="underline text-white/60 hover:text-[#5D8FC4] transition-colors duration-200" data-testid="replay-email-link">
                oscar@greatwesternfunding.com
              </a>
            </p>
          </div>
        </Reveal>

        <Reveal>
          <div className="mt-14 flex items-center gap-5 border border-white/10 bg-white/[0.02] p-6" data-testid="replay-host-card">
            <img src={OSCAR_URL} alt={HOST.name} className="w-16 h-16 rounded-full border-2 border-[#5D8FC4] object-cover shrink-0" />
            <div>
              <p className="font-bold">{HOST.name}</p>
              <p className="text-sm text-white/50">Owner & Broker, {HOST.company}</p>
              <p className="text-sm text-white/55 mt-1">Independent VA loan specialist — I personally handle every file, start to finish, no call center hand-offs.</p>
            </div>
          </div>
        </Reveal>

        <Reveal>
          <div className="mt-20">
            <h2 className="uppercase font-black tracking-tight text-xl md:text-2xl">Quick Questions</h2>
            <div className="mt-6 border-t border-white/10">
              {[
                ["Do I need to have watched live to book a call?", "No — the replay covers everything, and booking works the same either way."],
                ["Will booking a call pull my credit?", "No — this is a conversation about your numbers first. Nothing gets pulled until you say go."],
                ["Is there any cost?", "No, this session is completely free, no obligation."],
              ].map(([q, a], i) => (
                <div key={i} className="border-b border-white/10 py-5" data-testid={`replay-faq-${i + 1}`}>
                  <p className="font-bold text-[15px]"><span className="font-mono-c text-[#5D8FC4] mr-3">{String(i + 1).padStart(2, "0")}</span>{q}</p>
                  <p className="text-sm text-white/55 mt-2 pl-8 leading-relaxed">{a}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>

      <footer className="relative border-t border-white/10 px-6 pt-14 pb-32 overflow-hidden" data-testid="replay-footer">
        <div className="relative z-10 max-w-[960px] mx-auto">
          <div className="flex items-start gap-4">
            <House className="w-6 h-6 text-white/30 shrink-0 mt-1" strokeWidth={1.5} />
            <p className="text-[11px] text-white/30 leading-[1.8]">
              This is an educational webinar replay. Viewing does not constitute a loan application. No credit pull
              required. Results are not guaranteed. Oscar Tobar NMLS# 667612, Great Western Funding NMLS# 2739318. Equal Housing Opportunity.
            </p>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono-c text-[10px] text-white/25 tracking-widest">
            <span>© 2026 Great Western Funding · Equal Housing Lender</span>
            <Link to="/privacy-policy" className="underline hover:text-[#5D8FC4] transition-colors duration-200" data-testid="replay-privacy-link">Privacy Policy</Link>
            <Link to="/terms-conditions" className="underline hover:text-[#5D8FC4] transition-colors duration-200" data-testid="replay-terms-link">Terms & Conditions</Link>
            <Link to="/" className="underline hover:text-[#5D8FC4] transition-colors duration-200" data-testid="replay-home-link">Main Site</Link>
          </div>
        </div>
        <div className="footer-wordmark" aria-hidden="true">VA PURCHASING POWER</div>
      </footer>
    </div>
  );
}
